import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import { formatMoney } from '../utils/helpers';

const STAGES_MFC = { consultation:'Консультация', documents:'Документы', mfc:'МФЦ', procedure:'Процедура', ip_close:'Закрытие ИП', completed:'Завершено' };
const STAGES_COURT = { consultation:'Консультация', documents:'Документы', arbitrage:'Арбитраж', procedure:'Процедура', procedure_end:'Завершение процедуры', completed:'Завершено' };

function KpiCard({ value, label, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 140,
      boxShadow: 'var(--shadow)',
    }}>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || 'var(--text)', letterSpacing: '-0.03em' }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// «Что приносит деньги»: сводка по направлениям и по источникам (банкротство + гражданские дела), за всё время
function MoneyReport() {
  const rub = n => Math.round(Number(n) || 0).toLocaleString('ru-RU') + ' ₽';   // в сводке копейки только мешают
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/analytics/money').then(r => setD(r.data)).catch(() => setD({ rows: [], excluded_received: 0 })); }, []);
  if (!d) return null;
  const group = key => {
    const m = {};
    d.rows.forEach(r => {
      const a = m[r[key]] || (m[r[key]] = { name: r[key], leads: 0, contracts: 0, received: 0, received90: 0, expected: 0 });
      ['leads', 'contracts', 'received', 'received90', 'expected'].forEach(f => { a[f] += r[f]; });
    });
    return Object.values(m).sort((a, b) => b.received - a.received || b.leads - a.leads);
  };
  const th = { textAlign: 'right', padding: '0 0 8px 12px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const td = { textAlign: 'right', padding: '7px 0 7px 12px', borderTop: '1px solid var(--border)', fontSize: 13, whiteSpace: 'nowrap' };
  const Table = ({ title, rows }) => {
    const sum = f => rows.reduce((a, r) => a + r[f], 0);
    return (
      <div style={{ overflowX: 'auto', marginBottom: 18 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left', paddingLeft: 0 }}>{title}</th>
            <th style={th}>Обращений</th><th style={th}>Договоров</th><th style={th}>Получено</th>
            <th style={th}>На обращение</th><th style={th}>За 90 дней</th><th style={th}>Ещё ожидается</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 0, whiteSpace: 'normal' }}>{r.name}</td>
                <td style={td}>{r.leads}</td><td style={td}>{r.contracts}</td>
                <td style={{ ...td, fontWeight: 700 }}>{rub(r.received)}</td>
                <td style={td}>{r.leads ? rub(Math.round(r.received / r.leads)) : '—'}</td>
                <td style={td}>{rub(r.received90)}</td><td style={{ ...td, color: 'var(--text-muted)' }}>{rub(r.expected)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...td, textAlign: 'left', paddingLeft: 0, fontWeight: 700 }}>Итого</td>
              <td style={{ ...td, fontWeight: 700 }}>{sum('leads')}</td><td style={{ ...td, fontWeight: 700 }}>{sum('contracts')}</td>
              <td style={{ ...td, fontWeight: 700 }}>{rub(sum('received'))}</td>
              <td style={td}>{sum('leads') ? rub(Math.round(sum('received') / sum('leads'))) : '—'}</td>
              <td style={{ ...td, fontWeight: 700 }}>{rub(sum('received90'))}</td><td style={{ ...td, fontWeight: 700 }}>{rub(sum('expected'))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">Что приносит деньги</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '-4px 0 14px' }}>За всё время, по отмеченным оплатам. «На обращение» — сколько в среднем принёс один обратившийся.</div>
      <Table title="Направление" rows={group('direction')} />
      <Table title="Источник" rows={group('source')} />
      {d.excluded_received > 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Не вошло в таблицы: {rub(d.excluded_received)} от клиентов, исключённых из статистики.</div>}
    </div>
  );
}

function Funnel({ data }) {
  const steps = [
    { label: 'Консультации', val: data.consultations, color: 'var(--accent)' },
    { label: 'Думают / анализ', val: data.thinking || 0, color: 'var(--yellow)' },
    { label: 'Стали клиентами', val: data.became_clients, color: 'var(--green)' },
    { label: 'Завершено', val: data.completed, color: 'var(--text-muted)' },
  ];
  const max = Math.max(...steps.map(s => s.val), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.map((s, i) => {
        const pct = Math.round(s.val / max * 100);
        const convPct = i > 0 && data.consultations > 0 ? Math.round(s.val / data.consultations * 100) : null;
        return (
          <div key={s.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>
                {s.val}{convPct !== null ? <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>{convPct}%</span> : null}
              </span>
            </div>
            <div style={{ height: 8, background: 'var(--bg-surface)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: s.color, borderRadius: 4, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ data, stageMap, color }) {
  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  const convPct = data.consultations > 0 ? Math.round(data.became_clients / data.consultations * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard value={data.consultations} label="Консультаций" sub={data.no_show > 0 ? `${data.no_show} не явились` : null} />
        <KpiCard value={data.became_clients} label="Стали клиентами" color="var(--green)" />
        <KpiCard value={`${convPct}%`} label="Конверсия" color={convPct >= 50 ? 'var(--green)' : convPct >= 25 ? 'var(--yellow)' : 'var(--red)'} />
        <KpiCard value={formatMoney(data.collected)} label="Собрано" color="var(--accent)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Воронка */}
        <div className="card">
          <div className="card-title">Воронка</div>
          <Funnel data={data} />
        </div>

        {/* Этапы */}
        <div className="card">
          <div className="card-title">Активные по этапам</div>
          {data.by_stage.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.by_stage.map(s => (
                <div key={s.stage} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{stageMap[s.stage] || s.stage}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{s.count}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Нет активных</div>}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Средний чек</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{formatMoney(data.avg_check)}</span>
          </div>
        </div>
      </div>

      {/* График */}
      {data.dynamics.length > 0 && (
        <div className="card">
          <div className="card-title">Динамика за 6 месяцев</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.dynamics} barGap={4}>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }} />
              <Bar dataKey="consultations" name="Консультации" fill={color} radius={[4,4,0,0]} />
              <Bar dataKey="clients" name="Клиенты" fill="var(--green)" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Источники */}
      {data.by_source && data.by_source.length > 0 && (
        <div className="card">
          <div className="card-title">Источники обращений</div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0 0 10px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Источник</th>
                <th style={{ textAlign: 'center', padding: '0 0 10px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Обратились</th>
                <th style={{ textAlign: 'center', padding: '0 0 10px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Клиенты</th>
                <th style={{ textAlign: 'center', padding: '0 0 10px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Конверсия</th>
              </tr>
            </thead>
            <tbody>
              {data.by_source.map(s => {
                const conv = s.count > 0 ? Math.round(s.clients / s.count * 100) : 0;
                return (
                  <tr key={s.source} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 0', color: 'var(--text)' }}>{s.source}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.count}</td>
                    <td style={{ textAlign: 'center', color: 'var(--green)', fontWeight: 600 }}>{s.clients}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: conv >= 50 ? 'var(--green)' : conv >= 25 ? 'var(--yellow)' : 'var(--red)' }}>{conv}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsPage() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [tab, setTab] = useState('all');
  const [mfc, setMfc] = useState(null);
  const [court, setCourt] = useState(null);
  const [total, setTotal] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mfcR, courtR, totalR, refR] = await Promise.all([
        api.get('/analytics/monthly', { params: { year, month, type: 'mfc' } }),
        api.get('/analytics/monthly', { params: { year, month, type: 'court' } }),
        api.get('/analytics/monthly', { params: { year, month, type: 'all' } }),
        api.get('/analytics/referrals'),
      ]);
      setMfc(mfcR.data);
      setCourt(courtR.data);
      setTotal(totalR.data);
      setReferrals(refR.data);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [year, month]);

  const monthNames = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

  const exportExcel = async () => {
    const monthName = monthNames[Number(month)-1];
    const [mfcRes, courtRes] = await Promise.all([
      api.get('/analytics/monthly', { params: { year, month, type: 'mfc' } }),
      api.get('/analytics/monthly', { params: { year, month, type: 'court' } }),
    ]);
    const m = mfcRes.data, c = courtRes.data;
    const mfcPct = m.consultations>0?Math.round(m.became_clients/m.consultations*100):0;
    const courtPct = c.consultations>0?Math.round(c.became_clients/c.consultations*100):0;
    const wb = XLSX.utils.book_new();
    const summary = [
      ['Показатель','МФЦ','Судебное'],['Период',`${monthName} ${year}`,`${monthName} ${year}`],['','',''],
      ['Консультаций',m.consultations,c.consultations],['Не явились',m.no_show||0,c.no_show||0],
      ['Стали клиентами',m.became_clients,c.became_clients],['Конверсия',`${mfcPct}%`,`${courtPct}%`],
      ['Завершено',m.completed,c.completed],['Собрано за месяц (руб)',m.collected,c.collected],
      ['Средний чек (руб)',Math.round(m.avg_check),Math.round(c.avg_check)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Сводка');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Этап','Клиентов'],...m.by_stage.map(s=>[STAGES_MFC[s.stage]||s.stage,Number(s.count)])]), 'МФЦ этапы');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Этап','Клиентов'],...c.by_stage.map(s=>[STAGES_COURT[s.stage]||s.stage,Number(s.count)])]), 'Судебное этапы');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Месяц','Консультации','Клиенты'],...m.dynamics.map(d=>[d.month,Number(d.consultations),Number(d.clients)])]), 'МФЦ динамика');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Месяц','Консультации','Клиенты'],...c.dynamics.map(d=>[d.month,Number(d.consultations),Number(d.clients)])]), 'Судебное динамика');
    const [mfcSrc,courtSrc] = await Promise.all([api.get('/analytics/sources',{params:{type:'mfc'}}),api.get('/analytics/sources',{params:{type:'court'}})]);
    const srcData = [['Тип','Источник','Клиентов']];
    mfcSrc.data.by_source.forEach(s=>srcData.push(['МФЦ',s.source,Number(s.count)]));
    courtSrc.data.by_source.forEach(s=>srcData.push(['Судебное',s.source,Number(s.count)]));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(srcData), 'Источники');
    XLSX.writeFile(wb, `Аналитика_${monthName}_${year}.xlsx`);
  };

  const exportFull = async () => {
    const [mfcR,courtR] = await Promise.all([api.get('/analytics/all',{params:{type:'mfc'}}),api.get('/analytics/all',{params:{type:'court'}})]);
    const m=mfcR.data, c=courtR.data;
    const mfcPct=m.consultations>0?Math.round(m.became_clients/m.consultations*100):0;
    const courtPct=c.consultations>0?Math.round(c.became_clients/c.consultations*100):0;
    const wb = XLSX.utils.book_new();
    const summary = [
      ['Показатель','МФЦ','Судебное'],['Период','Все время','Все время'],['','',''],
      ['Консультаций',m.consultations,c.consultations],['Не явились',m.no_show||0,c.no_show||0],
      ['Стали клиентами',m.became_clients,c.became_clients],['Конверсия',`${mfcPct}%`,`${courtPct}%`],
      ['Завершено',m.completed,c.completed],['Собрано всего (руб)',m.collected,c.collected],
      ['Средний чек (руб)',Math.round(m.avg_check),Math.round(c.avg_check)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Сводка');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Этап','Клиентов'],...m.by_stage.map(s=>[STAGES_MFC[s.stage]||s.stage,Number(s.count)])]), 'МФЦ этапы');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Этап','Клиентов'],...c.by_stage.map(s=>[STAGES_COURT[s.stage]||s.stage,Number(s.count)])]), 'Судебное этапы');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Месяц','Консультации','Клиенты'],...m.dynamics.map(d=>[d.month,Number(d.consultations),Number(d.clients)])]), 'МФЦ динамика');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Месяц','Консультации','Клиенты'],...c.dynamics.map(d=>[d.month,Number(d.consultations),Number(d.clients)])]), 'Судебное динамика');
    const srcData = [['Тип','Источник','Обратились','Стали клиентами','Конверсия %']];
    m.by_source.forEach(s=>srcData.push(['МФЦ',s.source,Number(s.count),Number(s.clients),s.count>0?Math.round(s.clients/s.count*100):0]));
    c.by_source.forEach(s=>srcData.push(['Судебное',s.source,Number(s.count),Number(s.clients),s.count>0?Math.round(s.clients/s.count*100):0]));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(srcData), 'Источники');
    XLSX.writeFile(wb, 'Аналитика_все_время.xlsx');
  };

  const TABS = [
    { key: 'all',      label: 'Сводно' },
    { key: 'mfc',      label: 'МФЦ-банкротство' },
    { key: 'court',    label: 'Судебное' },
    { key: 'referral', label: 'Рефералы' },
  ];

  const currentData = tab === 'all' ? total : tab === 'mfc' ? mfc : tab === 'court' ? court : null;
  const currentStageMap = tab === 'court' ? STAGES_COURT : { ...STAGES_MFC, ...STAGES_COURT };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Аналитика</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="form-select" style={{ width: 'auto' }} value={month} onChange={e => setMonth(e.target.value)}>
            {monthNames.map((m, i) => <option key={i+1} value={String(i+1)}>{m}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto' }} value={year} onChange={e => setYear(e.target.value)}>
            {[2024,2025,2026].map(y => <option key={y} value={String(y)}>{y}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={exportExcel}>Excel за месяц</button>
          <button className="btn btn-secondary" onClick={exportFull}>Excel за всё время</button>
        </div>
      </div>

      <div className="tabs">
        {TABS.map(t => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', padding: 20 }}>Загрузка...</div>}

      {!loading && tab === 'all' && <MoneyReport />}

      {!loading && tab !== 'referral' && (
        <Section data={currentData} stageMap={currentStageMap} color="var(--accent)" />
      )}

      {!loading && tab === 'referral' && referrals && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <KpiCard value={referrals.referral_clients} label="Клиентов по рекомендации" />
            <KpiCard value={`${referrals.referral_pct}%`} label="От всех клиентов" color="var(--accent)" />
            <KpiCard value={formatMoney(referrals.paid_cash)} label="Выплачено деньгами" color="var(--green)" />
            <KpiCard value={formatMoney(referrals.paid_discount)} label="Скидок предоставлено" />
            {referrals.pending_cash > 0 && (
              <KpiCard value={formatMoney(referrals.pending_cash)} label="Ожидает выплаты" color="var(--yellow)" />
            )}
          </div>
          {referrals.top_referrers.length > 0 && (
            <div className="card">
              <div className="card-title">Топ рекомендателей</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {referrals.top_referrers.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>{r.name}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)' }}>{r.count} клиентов</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
